
import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  MoreHorizontal, 
  Trash2, 
  Mail, 
  User,
  UserX,
  ShieldAlert
} from 'lucide-react';
import { MasterUser } from '@/types/userRole';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import AssignRoleDialog from './AssignRoleDialog';
import EditUserDialog from './EditUserDialog';

interface UsersTableProps {
  users: MasterUser[];
  isLoading: boolean;
  onRemove: (userId: string) => Promise<void>;
  onRoleUpdate?: (userId: string, newRole: string) => Promise<void>;
  onStatusUpdate?: (userId: string, newStatus: 'active' | 'suspended') => Promise<void>;
  onResendInvitation?: (userId: string) => Promise<void>;
}

const UsersTable: React.FC<UsersTableProps> = ({ 
  users, 
  isLoading, 
  onRemove,
  onRoleUpdate,
  onStatusUpdate,
  onResendInvitation
}) => {
  const [processingUserId, setProcessingUserId] = useState<string | null>(null);
  const [userToAssignRole, setUserToAssignRole] = useState<MasterUser | null>(null);
  const [userToEdit, setUserToEdit] = useState<MasterUser | null>(null);
  const [resendingInvitation, setResendingInvitation] = useState<string | null>(null);
  const [togglingUserStatus, setTogglingUserStatus] = useState<string | null>(null);
  const { toast } = useToast();

  const handleRemoveUser = async (userId: string) => {
    setProcessingUserId(userId);
    try {
      await onRemove(userId);
      toast({
        title: "User Removed",
        description: "User has been successfully removed",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to remove user",
        variant: "destructive",
      });
    } finally {
      setProcessingUserId(null);
    }
  };

  const handleResendInvitation = async (userId: string) => {
    setResendingInvitation(userId);
    try {
      if (onResendInvitation) {
        await onResendInvitation(userId);
      } else {
        // Mock the API call
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      toast({
        title: "Invitation Resent",
        description: "Invitation has been resent successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to resend invitation",
        variant: "destructive",
      });
    } finally {
      setResendingInvitation(null);
    }
  };

  const handleToggleUserStatus = async (userId: string, currentStatus: string) => {
    setTogglingUserStatus(userId);
    try {
      const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
      
      if (onStatusUpdate) {
        await onStatusUpdate(userId, newStatus as 'active' | 'suspended');
      } else {
        // Mock the API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Update the user's status in the UI
        const userToUpdate = users.find(user => user.id === userId);
        if (userToUpdate) {
          userToUpdate.status = newStatus as 'active' | 'suspended';
        }
      }
      
      toast({
        title: "Status Updated",
        description: `User status is now ${currentStatus === 'active' ? 'suspended' : 'active'}`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update user status",
        variant: "destructive",
      });
    } finally {
      setTogglingUserStatus(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'suspended':
        return <Badge className="bg-red-100 text-red-800">Suspended</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center my-8">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div>
      {users.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">No users found</p>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {user.role || 'User'}
                    </Badge>
                  </TableCell>
                  <TableCell>{getStatusBadge(user.status)}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem 
                          onClick={() => setUserToAssignRole(user)}
                          className="cursor-pointer"
                        >
                          <ShieldAlert className="h-4 w-4 mr-2" />
                          Assign Role
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => setUserToEdit(user)}
                          className="cursor-pointer"
                        >
                          <User className="h-4 w-4 mr-2" />
                          Edit User
                        </DropdownMenuItem>
                        {user.status === 'active' && (
                          <DropdownMenuItem 
                            onClick={() => handleToggleUserStatus(user.id, user.status)}
                            className="cursor-pointer text-yellow-600"
                            disabled={togglingUserStatus === user.id}
                          >
                            <UserX className="h-4 w-4 mr-2" />
                            {togglingUserStatus === user.id ? 'Suspending...' : 'Suspend User'}
                          </DropdownMenuItem>
                        )}
                        {user.status === 'suspended' && (
                          <DropdownMenuItem 
                            onClick={() => handleToggleUserStatus(user.id, user.status)}
                            className="cursor-pointer text-green-600"
                            disabled={togglingUserStatus === user.id}
                          >
                            <User className="h-4 w-4 mr-2" />
                            {togglingUserStatus === user.id ? 'Activating...' : 'Activate User'}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        {user.status === 'pending' && (
                          <DropdownMenuItem 
                            onClick={() => handleResendInvitation(user.id)}
                            className="cursor-pointer"
                            disabled={resendingInvitation === user.id}
                          >
                            <Mail className="h-4 w-4 mr-2" />
                            {resendingInvitation === user.id ? 'Resending...' : 'Resend Invitation'}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem 
                          onClick={() => handleRemoveUser(user.id)}
                          disabled={processingUserId === user.id}
                          className="cursor-pointer text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {processingUserId === user.id ? 'Removing...' : 'Remove User'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {userToAssignRole && (
        <AssignRoleDialog 
          open={!!userToAssignRole}
          user={userToAssignRole}
          onClose={() => setUserToAssignRole(null)}
          onRoleUpdate={onRoleUpdate}
        />
      )}

      {userToEdit && (
        <EditUserDialog 
          open={!!userToEdit}
          user={userToEdit}
          onClose={() => setUserToEdit(null)}
        />
      )}
    </div>
  );
};

export default UsersTable;
